import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { Decision } from '@kkp-fraud/shared';
import { ApiError, apiClientsApi } from '../api';
import type { ApiReferenceInfo, ApiTryResult } from '../apiClients';
import {
  BODY_FIELDS,
  ENDPOINTS,
  HEALTHZ_FIELDS,
  LANGUAGES,
  PREDETECT_HEADERS,
  PREDETECT_STATUSES,
  READYZ_FIELDS,
  RESPONSE_FIELDS,
  apiBaseOf,
  buildBody,
  codeExample,
  newTransactionId,
  postmanFileName,
  sampleValues,
  type BodyField,
  type Endpoint,
  type EndpointId,
  type ExampleValues,
  type LanguageId,
  type PredetectBody,
} from '../apiReference';
import { useShell } from '../components/AppLayout';
import { DecisionChip } from '../components/Chips';
import { t } from '../i18n';
import { SOURCE_IDS } from '../sources';

/**
 * API Keys → API reference (Admin only, like the screen): the decision API's three public routes —
 * the URL, the headers, every Transaction field of KKP's sheet with an editable example value
 * (KKP's sample, a fresh `transaction_id`), the response and its statuses — and code examples in
 * five languages built live from the edited values. Every example says `YOUR_API_KEY`; a real key
 * only ever sits in the Try-it field, which the examples never read.
 */
export function ApiReferencePanel() {
  const { settings, showToast } = useShell();
  const [info, setInfo] = useState<ApiReferenceInfo | null>(null);
  const [endpointId, setEndpointId] = useState<EndpointId>('predetect');
  const [values, setValues] = useState<ExampleValues>(() => sampleValues());
  const [lang, setLang] = useState<LanguageId>('curl');

  useEffect(() => {
    let live = true;
    apiClientsApi.reference(settings).then(
      (r) => live && setInfo(r),
      () => live && setInfo(null),
    );
    return () => {
      live = false;
    };
  }, [settings]);

  const endpoint = ENDPOINTS.find((e) => e.id === endpointId)!;
  const post = endpoint.method === 'POST';
  const body = useMemo(() => buildBody(values), [values]);
  const base = info?.apiBase ?? '';
  const url = `${apiBaseOf(base)}${endpoint.path}`;
  const code = codeExample(lang, endpoint, base, post ? body : undefined);

  const copy = (text: string) => {
    const done = navigator.clipboard?.writeText(text);
    if (!done) {
      showToast(t('apiref.copy.failed'), 'error');
      return;
    }
    void done.then(
      () => showToast(t('apikeys.key.copied')),
      () => showToast(t('apiref.copy.failed'), 'error'),
    );
  };
  const setField = (key: string, value: string) => setValues((v) => ({ ...v, transaction: { ...v.transaction, [key]: value } }));
  const freshId = () => setField('transaction_id', newTransactionId());

  return (
    <div className="apiref" data-testid="apiref">
      <nav className="tabs" role="tablist" aria-label={t('apiref.endpoints.a11y')}>
        {ENDPOINTS.map((e) => (
          <button
            key={e.id}
            type="button"
            role="tab"
            className="tab"
            aria-selected={e.id === endpointId}
            data-testid={`apiref-endpoint-${e.id}`}
            onClick={() => setEndpointId(e.id)}
          >
            <MethodChip endpoint={e} /> <span className="mono">{e.path}</span>
          </button>
        ))}
      </nav>

      <div className="apiref-intro">
        <h2>{t(`apiref.endpoint.${endpoint.id}.title`)}</h2>
        <p>{t(`apiref.endpoint.${endpoint.id}.lead`)}</p>
        <span className={endpoint.auth ? 'chip warn' : 'chip mute'} data-testid="apiref-auth">
          {t(endpoint.auth ? 'apiref.auth.key' : 'apiref.auth.none')}
        </span>
      </div>

      <div className="apiref-grid">
        <div className="apiref-main">
          <section className="apiref-block" aria-labelledby="apiref-params-h">
            <h3 id="apiref-params-h">{t('apiref.params.title')}</h3>
            <div className="apiref-label">{t('apiref.url.label')}</div>
            <div className="apiref-url">
              <MethodChip endpoint={endpoint} />
              <code data-testid="apiref-url">{url}</code>
              <CopyIcon label={t('apiref.copy.url')} testid="apiref-url-copy" onCopy={() => copy(url)} />
            </div>
            {!info && (
              <div className="small muted" data-testid="apiref-base-unknown">
                {t('apiref.url.unknown')}
              </div>
            )}

            {post && (
              <>
                <h4 className="apiref-sub">{t('apiref.headers.title')}</h4>
                <div className="apiref-params">
                  {PREDETECT_HEADERS.map((h) => (
                    <div key={h.id} className="apiref-param" data-testid={`apiref-header-${h.id}`}>
                      <div className="apiref-param-name">
                        <code>{h.name}</code>
                        <TypeChip type="string" />
                        <RequiredChip required={h.required} />
                        <div className="small muted">{t(`apiref.header.${h.id}.desc`)}</div>
                      </div>
                      <div className="apiref-param-value">
                        {h.value ? <code className="apiref-static">{h.value}</code> : <span className="small muted">{t('apiref.header.request_id.example')}</span>}
                        {h.value && <CopyIcon label={t('apiref.copy.value', { name: h.name })} testid={`apiref-header-copy-${h.id}`} onCopy={() => copy(h.value)} />}
                      </div>
                    </div>
                  ))}
                </div>

                <h4 className="apiref-sub">{t('apiref.body.title')}</h4>
                <p className="small muted apiref-note">{t('apiref.body.lead')}</p>
                <div className="apiref-params" data-testid="apiref-body-fields">
                  <ParamRow
                    field="Channel.Id"
                    detail={t('apiref.field.channel_id')}
                    required={false}
                    value={values.channelId}
                    onChange={(v) => setValues((x) => ({ ...x, channelId: v }))}
                    onCopy={copy}
                  />
                  {BODY_FIELDS.map((f) => (
                    <ParamRow
                      key={f.key}
                      field={`Transaction.${f.key}`}
                      doc={f}
                      required={f.required}
                      value={values.transaction[f.key] ?? ''}
                      onChange={(v) => setField(f.key, v)}
                      onCopy={copy}
                      extra={
                        f.key === 'transaction_id' ? (
                          <button type="button" className="btn small" data-testid="apiref-new-txid" onClick={freshId}>
                            {t('apiref.txid.new')}
                          </button>
                        ) : undefined
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </section>

          <ResponseDoc endpoint={endpoint} />
        </div>

        <aside className="apiref-side">
          <section className="apiref-block apiref-code" aria-labelledby="apiref-code-h" data-testid="apiref-code-panel">
            <div className="apiref-code-head">
              <h3 id="apiref-code-h">{t('apiref.code.title')}</h3>
              <span className="spacer" />
              <CopyIcon label={t('apiref.copy.code')} testid="apiref-code-copy" onCopy={() => copy(code)} />
            </div>
            <div className="segbar apiref-langs" role="group" aria-label={t('apiref.lang.a11y')}>
              {LANGUAGES.map((l) => (
                <button key={l} type="button" aria-pressed={l === lang} data-testid={`apiref-lang-${l}`} onClick={() => setLang(l)}>
                  {t(`apiref.lang.${l}`)}
                </button>
              ))}
            </div>
            <CodeBlock code={code} testid="apiref-code" label={t(`apiref.lang.${lang}`)} />
            <p className="small muted apiref-note" data-testid="apiref-code-note">
              {t(lang === 'postman' ? 'apiref.postman.note' : 'apiref.code.key_note')}
            </p>
            {lang === 'postman' && (
              <button type="button" className="btn small" data-testid="apiref-postman-download" onClick={() => download(postmanFileName(endpoint), code)}>
                {t('apiref.postman.download')}
              </button>
            )}
            {post && (
              <p className="small apiref-note" data-testid="apiref-idempotency">
                {t('apiref.txid.note')}
              </p>
            )}
          </section>

          {post && <TryIt body={body} tryPerMinute={info?.tryPerMinute} onNewId={freshId} />}
        </aside>
      </div>
    </div>
  );
}

function MethodChip({ endpoint }: { endpoint: Endpoint }) {
  return <span className={`apiref-method ${endpoint.method === 'POST' ? 'post' : 'get'}`}>{endpoint.method}</span>;
}

function TypeChip({ type }: { type: 'string' | 'number' | 'boolean' | 'array' }) {
  return <span className="chip mute apiref-type" title={t('apiref.col.type')}>{t(`apiref.type.${type}`)}</span>;
}

function RequiredChip({ required }: { required: boolean }) {
  return required ? <span className="chip warn">{t('apiref.required')}</span> : <span className="chip mute">{t('apiref.optional')}</span>;
}

/** One editable body field: its key, KKP's name / DE / sources, a type chip, the example value and a copy button. */
function ParamRow({ field, doc, detail, required, value, onChange, onCopy, extra }: {
  field: string;
  doc?: BodyField;
  detail?: string;
  required: boolean;
  value: string;
  onChange: (v: string) => void;
  onCopy: (text: string) => void;
  extra?: ReactNode;
}) {
  const id = `apiref-value-${field.replace(/\W/g, '-')}`;
  return (
    <div className="apiref-param" data-testid={`apiref-field-${doc?.key ?? 'channel_id'}`}>
      <div className="apiref-param-name">
        <label htmlFor={id}>
          <code>{field}</code>
        </label>
        <TypeChip type="string" />
        <RequiredChip required={required} />
        <div className="small muted">
          {doc ? doc.name : detail}
          {doc?.de && <span className="mono"> · {doc.de}</span>}
        </div>
        {doc && (
          <div className="apiref-sends" title={t('apiref.col.sources')}>
            {SOURCE_IDS.filter((s) => doc.sends[s]).map((s) => (
              <span key={s} className="chip info">
                {t(`source.${s}.name`)}
              </span>
            ))}
          </div>
        )}
        {doc && doc.sample === '' && <div className="small muted">{t('apiref.field.not_in_sample')}</div>}
      </div>
      <div className="apiref-param-value">
        <input
          id={id}
          type="text"
          className="mono-input"
          value={value}
          spellCheck={false}
          autoComplete="off"
          aria-label={t('apiref.example.a11y', { field })}
          onChange={(e) => onChange(e.target.value)}
        />
        <CopyIcon label={t('apiref.copy.value', { name: field })} testid={`apiref-copy-${doc?.key ?? 'channel_id'}`} onCopy={() => onCopy(value)} />
        {extra}
      </div>
    </div>
  );
}

function ResponseDoc({ endpoint }: { endpoint: Endpoint }) {
  if (endpoint.id !== 'predetect') {
    const keys = endpoint.id === 'healthz' ? HEALTHZ_FIELDS : READYZ_FIELDS;
    const ns = endpoint.id === 'healthz' ? 'health' : 'ready';
    return (
      <section className="apiref-block" aria-labelledby="apiref-resp-h" data-testid="apiref-response">
        <h3 id="apiref-resp-h">{t('apiref.response.title')}</h3>
        <p className="small muted apiref-note">{t(`apiref.response.${endpoint.id}.lead`)}</p>
        <dl className="apiref-dl">
          {keys.map((k) => (
            <div key={k}>
              <dt>
                <code>{k}</code>
              </dt>
              <dd>{t(`apiref.${ns}.${k}`)}</dd>
            </div>
          ))}
        </dl>
      </section>
    );
  }
  return (
    <>
      <section className="apiref-block" aria-labelledby="apiref-resp-h" data-testid="apiref-response">
        <h3 id="apiref-resp-h">{t('apiref.response.title')}</h3>
        <p className="small muted apiref-note">{t('apiref.response.predetect.lead')}</p>
        <dl className="apiref-dl">
          {Object.entries(RESPONSE_FIELDS).map(([k, d]) => (
            <div key={k} data-testid={`apiref-resp-${k}`}>
              <dt>
                <code>{k}</code> <TypeChip type={d.type} />
                {!d.always && <span className="chip mute">{t('apiref.response.when')}</span>}
              </dt>
              <dd>{t(`apiref.resp.${k}`)}</dd>
            </div>
          ))}
        </dl>
      </section>
      <section className="apiref-block" aria-labelledby="apiref-status-h" data-testid="apiref-statuses">
        <h3 id="apiref-status-h">{t('apiref.status.title')}</h3>
        <dl className="apiref-dl">
          {PREDETECT_STATUSES.map((s) => (
            <div key={s}>
              <dt>
                <span className={s === '200' ? 'chip ok' : 'chip bad'}>{s}</span>
              </dt>
              <dd>{t(`apiref.status.${s}`)}</dd>
            </div>
          ))}
        </dl>
      </section>
    </>
  );
}

/** A dark code block with line numbers (CSS counters, so a copy or `textContent` is the code alone). */
export function CodeBlock({ code, testid, label }: { code: string; testid: string; label?: string }) {
  return (
    <pre className="codeblock" data-testid={testid} aria-label={label} tabIndex={0}>
      <code>
        {code.split('\n').map((line, i) => (
          <span key={i} className="codeline">
            {line}
            {'\n'}
          </span>
        ))}
      </code>
    </pre>
  );
}

function CopyIcon({ label, testid, onCopy }: { label: string; testid: string; onCopy: () => void }) {
  return (
    <button type="button" className="btn small apiref-copy" aria-label={label} title={label} data-testid={testid} onClick={onCopy}>
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="12" height="12" rx="2" />
        <path d="M5 15V5a2 2 0 0 1 2-2h10" />
      </svg>
    </button>
  );
}

function download(name: string, text: string) {
  if (typeof URL.createObjectURL !== 'function') return;
  const href = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const a = document.createElement('a');
  a.href = href;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(href), 0);
}

const DECISIONS: readonly Decision[] = ['APPROVE', 'APPROVE_NOTIFY', 'STEP_UP', 'DECLINE'];

/**
 * Try it (ยิงทดสอบ): paste a key, send the example body through the admin service to the decision
 * API, read the answer. The key lives in this component's state only — never in the examples,
 * never in a URL, and the admin service neither stores nor logs it.
 */
function TryIt({ body, tryPerMinute, onNewId }: { body: PredetectBody; tryPerMinute: number | undefined; onNewId: () => void }) {
  const { settings } = useShell();
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApiTryResult | null>(null);
  // A new answer is brought into view: it lands under the key field, often below the fold.
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (result) resultRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [result]);

  const send = async () => {
    if (key.trim() === '') {
      setError(t('apiref.try.key.required'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setResult(await apiClientsApi.tryPredetect(settings, { key: key.trim(), body }));
    } catch (e) {
      setResult(null);
      setError(tryErrorText(e));
    } finally {
      setBusy(false);
    }
  };

  const answer = result?.body as { decision?: string; transaction_id?: string } | null | undefined;
  const decision = DECISIONS.find((d) => d === answer?.decision);
  const ok = result !== null && result.status >= 200 && result.status < 300;

  return (
    <section className="apiref-block apiref-try" aria-labelledby="apiref-try-h" data-testid="apiref-try">
      <h3 id="apiref-try-h">{t('apiref.try.title')}</h3>
      <div className="banner warn" role="note" data-testid="apiref-try-warning">
        {t('apiref.try.warning')}
      </div>
      <div className="field">
        <label htmlFor="apiref-try-key">{t('apiref.try.key')}</label>
        <input
          id="apiref-try-key"
          type="password"
          className="mono-input"
          value={key}
          autoComplete="off"
          spellCheck={false}
          data-testid="apiref-try-key"
          placeholder={t('apiref.try.key.placeholder')}
          onChange={(e) => setKey(e.target.value)}
        />
        <div className="small muted">{t('apiref.try.key.hint')}</div>
      </div>
      <div className="row-wrap" style={{ gap: 8 }}>
        <button type="button" className="btn primary" data-testid="apiref-try-send" disabled={busy} onClick={() => void send()}>
          {busy ? t('apiref.try.sending') : t('apiref.try.send')}
        </button>
        <button type="button" className="btn" data-testid="apiref-try-new-txid" disabled={busy} onClick={onNewId}>
          {t('apiref.txid.new')}
        </button>
        <span className="small muted mono" data-testid="apiref-try-txid">
          {body.Transaction.transaction_id ?? ''}
        </span>
      </div>
      {tryPerMinute !== undefined && <div className="small muted">{t('apiref.try.cap', { n: tryPerMinute })}</div>}
      {error && (
        <div className="why-missing" role="alert" data-testid="apiref-try-error" style={{ margin: 0 }}>
          {error}
        </div>
      )}
      {result && (
        <div ref={resultRef} className="apiref-result" data-testid="apiref-try-result" role="status">
          <div className="row-wrap" style={{ gap: 6, alignItems: 'center' }}>
            <span className={ok ? 'chip ok' : 'chip bad'} data-testid="apiref-try-status">
              {t('apiref.try.status', { status: result.status })}
            </span>
            {decision && <DecisionChip decision={decision} />}
            <span className="chip mute" data-testid="apiref-try-latency">
              {t('apiref.try.latency', { ms: result.latencyMs })}
            </span>
            {result.replay && (
              <span className="chip warn" data-testid="apiref-try-replay" title={t('apiref.try.replay.tip')}>
                {t('apiref.try.replay')}
              </span>
            )}
          </div>
          {result.status === 401 && <div className="small">{t('apiref.try.unauthorized')}</div>}
          <CodeBlock code={pretty(result.body)} testid="apiref-try-body" label={t('apiref.try.body')} />
        </div>
      )}
    </section>
  );
}

function pretty(body: unknown): string {
  return typeof body === 'string' ? body : JSON.stringify(body, null, 2);
}

function tryErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === 'try_rate_limited') return t('apiref.try.error.rate_limited');
    if (e.code === 'upstream_unreachable') return t('apiref.try.error.unreachable');
    return e.message;
  }
  return e instanceof Error ? e.message : String(e);
}
